/**
 * Type definitions for the Modules (Module 360) feature area.
 *
 * There is no generated types file and no shared types package in this repo, so
 * — exactly as `views/clients/clients.types.ts` does — these records are
 * hand-written mirrors of the API TypeBox schemas, with the schema they mirror
 * cited here:
 *
 *   ModuleCatalogRecord  → packages/api/src/services/organization/module-catalog/
 *                          module-catalog.schema.ts
 *                          (migrations 20260712000001_create_module_catalog.ts +
 *                          20260815000001_extend_module_catalog_identity.ts)
 *   ModuleOwnerRecord    → packages/api/src/services/organization/module-owners/
 *                          module-owners.schema.ts
 *                          (migration 20260815000004_create_module_owners.ts)
 *   ModuleGovernanceFact → packages/api/src/services/organization/module-governance/
 *                          module-governance.schema.ts
 *                          (migration 20260815000002_create_module_governance.ts)
 *   ModuleReleaseRecord  → packages/api/src/services/organization/module-releases/
 *                          module-releases.schema.ts
 *                          (migration 20260815000003_create_module_releases.ts)
 *   ModuleSummaryRecord  → packages/api/src/services/organization/module-summary/
 *                          module-summary.schema.ts   (derived, find-only)
 *   ModuleAdoptionRow    → packages/api/src/services/organization/module-adoption/
 *                          module-adoption.schema.ts  (derived, find-only)
 *   ModuleActivityItem   → packages/api/src/services/organization/module-activity/
 *                          module-activity.schema.ts  (derived, find-only)
 *   ModuleActionItemRecord → packages/api/src/services/read-ai/read-ai-action-items/
 *                          read-ai-action-items.class.ts `ReadAiActionItemRecord`
 *                          (no TypeBox schema — the service is a hand-rolled
 *                          read-only query class and `rowToRecord` IS the
 *                          contract; it is a WHITELIST, so a column absent from
 *                          it never reaches the browser)
 *
 * STALE TYPE WARNING — read before importing anything module-shaped:
 * `views/clients/clients.types.ts` ALSO declares a `ModuleCatalogRecord`. That
 * one predates `kind` and the governed identity block (`governedRepo`,
 * `category`, `domainRole`, `status`, `aliases`, plus the 20260815000001
 * additions `componentOf`, `packages`, `legacyAliases`, `configureSymbol`,
 * `configureSymbolSource`, `catalogUpdated`); it carries only the handful of
 * columns the Client 360 assignment picker reads — `assignable` among them,
 * because that picker is a RESOLVE surface and has to filter on it. It is
 * deliberately NOT extended here: a partial record widened by declaration
 * merging would leave the governed fields optional-by-accident. The FULL record
 * is defined below and this file is the one to import from anywhere the
 * governed identity matters.
 *
 * VOCABULARY (Module-Coordinator docs/CATALOG.md; HQ mirrors, never invents):
 *   governedSlug — the Coordinator `slug` (e.g. `quality-control`). THE key
 *                  every module-scoped table joins on. The catalog sync writes
 *                  it into `module_catalog.moduleKey`, so for a governed row
 *                  `moduleKey` IS the slug (see {@link governedSlugOf}).
 *   governedRepo — `intrasync-<slug>`. Carried for portability/display; never
 *                  the join key in the module-scoped tables.
 *   assignable   — may a CLIENT be assigned this entry? THE GATE. Every LIST
 *                  surface (this feature area included) shows every row;
 *                  anything that RESOLVES a spelling to a governed identity
 *                  must filter `assignable = 1`.
 *   componentOf  — non-null ⇒ this entry is a component inside another repo
 *                  (`cast-stone` → `designer`). Anything resolving a slug to a
 *                  REPO-level fact (audit score, branch, release, DevOps link)
 *                  must skip these entries; identity facts (packages, display
 *                  name, domain role) work on components normally. Surface it
 *                  in the UI rather than silently emitting wrong data.
 */

// ---------------------------------------------------------------------------
// module-catalog — the governed catalog entry (module | tool | integration | …)
// ---------------------------------------------------------------------------

/**
 * `module_catalog.kind` — which of the things HQ tracks work against this entry
 * names. `core` and `library` are listed-but-not-assignable platform entries.
 * Widened with `string` because the column is a free NVARCHAR (service-level
 * vocabulary, not a DB CHECK).
 */
export type ModuleKind =
  | "module"
  | "tool"
  | "integration"
  | "core"
  | "library"
  | string;

/** The Coordinator's own `category` for the entry (mirrored verbatim). */
export type ModuleCategory = ModuleKind;

/** `module_catalog.status` — the Coordinator lifecycle stamp. */
export type ModuleCatalogStatus = "active" | "planned" | "deprecated" | string;

/** Where `configureSymbol` came from (`configureSymbolSource`). */
export type ConfigureSymbolSource = "registry" | "derived" | "none" | string;

/**
 * One entry in the governed catalog — the full `module_catalog` row.
 *
 * The governed identity block (everything from `governedRepo` down to
 * `catalogUpdated`) is SYNC-OWNED: `yarn catalog:sync` is the only writer, and
 * those fields are deliberately absent from the API's data/patch schemas, so no
 * UI can mint or edit a governed identity (Coordinator decision D3). Treat them
 * as read-only here.
 *
 * `aliases`, `packages` and `legacyAliases` are NVARCHAR(MAX) JSON ARRAYS —
 * they arrive as strings. Read them through {@link parseJsonArrayColumn}.
 */
export interface ModuleCatalogRecord {
  id: string;
  /**
   * The catalog key. For a governed row this IS the Coordinator slug (the sync
   * upserts `moduleKey = entry.slug`); ungoverned legacy rows keep whatever
   * free text they were created with.
   */
  moduleKey: string;
  kind: ModuleKind;
  displayName: string;
  description?: string | null;
  /** Default for `client_modules.isDeployable` when assigning from the catalog. */
  isDeployable: boolean;
  sortOrder?: number | null;

  // ---- governed identity (sync-owned; read-only through the service) ------
  /** `intrasync-<slug>` — non-empty ⇒ the resolver treats this row as an anchor. */
  governedRepo?: string | null;
  category?: ModuleCategory | null;
  /** One-line domain description (Coordinator docs/DOMAIN.md). */
  domainRole?: string | null;
  status?: ModuleCatalogStatus | null;
  /** JSON array of the LIVE alias spellings (the resolver's pass-2 table). */
  aliases?: string | null;
  /**
   * THE GATE — may a client be assigned this entry? LIST all, RESOLVE these.
   *
   * OPTIONAL on purpose: the column arrives with migration `20260815000001`, and
   * web/api ship as separate containers, so a web build can reach an API that
   * has not migrated yet. Read it as `assignable !== false` — an ABSENT field
   * means assignable by construction (every row a pre-migration API can return
   * was synced under the old assignable-only rule). A truthy check instead would
   * render every entry "not assignable" and report zero assignable during that
   * window. Same reasoning the API uses in `mcp-resolve.ts`.
   */
  assignable?: boolean;
  /** Non-null ⇒ a component inside another repo; skip repo-level facts. */
  componentOf?: string | null;
  /** JSON array of the npm packages this entry ships. */
  packages?: string | null;
  /** JSON array of RETIRED spellings — distinct from the live `aliases`. */
  legacyAliases?: string | null;
  configureSymbol?: string | null;
  configureSymbolSource?: ConfigureSymbolSource | null;
  /** `catalog.updated` day-stamp this row was mirrored from (staleness). */
  catalogUpdated?: string | null;

  // ---- standard audit fields ---------------------------------------------
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

/**
 * The governed slug for a catalog row — the key every module-scoped service
 * queries by, and the `slug` param of the ModuleDetail route.
 *
 * Returns `moduleKey` VERBATIM. HQ deliberately does not re-derive or
 * re-normalise a slug on the web: the API owns one normaliser
 * (`module-resolver.ts` `normalizeModuleKey`) and a second, forked copy here
 * would silently change what a mirrored HQ can resolve.
 */
export function governedSlugOf(record: Pick<ModuleCatalogRecord, "moduleKey">): string {
  return record.moduleKey;
}

/** A row the Coordinator governs (has a repo) vs. free-text legacy vocabulary. */
export function isGoverned(record: Pick<ModuleCatalogRecord, "governedRepo">): boolean {
  return !!record.governedRepo?.trim();
}

/**
 * Read one of the JSON-array columns (`aliases` / `packages` / `legacyAliases`).
 * The sync sorts before `JSON.stringify` so re-runs diff byte-stable; a
 * malformed or absent value yields `[]` rather than throwing, because a broken
 * mirror must never blank a page.
 */
export function parseJsonArrayColumn(value?: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Every spelling a governed entry has EVER been known by — its slug, its live
 * `aliases` and its retired `legacyAliases` — deduped, primary slug first.
 *
 * WHY THIS EXISTS: anything stamped with a governed slug records the spelling
 * that was current WHEN IT WAS WRITTEN. A rename mints a new slug and pushes
 * the old one into `legacyAliases`; rows written before the rename keep the old
 * spelling forever (nothing back-fills them). So a consumer querying by the
 * primary slug alone silently UNDER-REPORTS after any rename — it looks like a
 * quiet module rather than a renamed one.
 *
 * The API deliberately does no alias resolution of its own on these queries
 * (`read-ai-action-items` takes the spellings it is given): a second resolver
 * beside `module-resolver.ts` would fork governed identity. The caller resolves
 * the spellings — here — and passes the set.
 *
 * Uses {@link parseJsonArrayColumn} for both JSON columns; a malformed mirror
 * degrades to "just the slug" rather than throwing.
 */
export function governedSlugSpellings(
  record: Pick<ModuleCatalogRecord, "moduleKey" | "aliases" | "legacyAliases">,
): string[] {
  const spellings = new Set<string>();
  const add = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) spellings.add(trimmed);
  };
  // The primary slug is ALWAYS included, even when the alias columns are empty
  // or unparseable — an empty set is a "match nothing" instruction to the API.
  add(governedSlugOf(record));
  for (const alias of parseJsonArrayColumn(record.aliases)) add(alias);
  for (const alias of parseJsonArrayColumn(record.legacyAliases)) add(alias);
  return [...spellings];
}

// ---------------------------------------------------------------------------
// module-owners — WHO owns a catalog entry (the module-side `client_contacts`)
// ---------------------------------------------------------------------------

/** `module_owners.role` — service-level vocabulary, not a DB CHECK. */
export const MODULE_OWNER_ROLES = [
  { key: "owner", label: "Owner" },
  { key: "maintainer", label: "Maintainer" },
  { key: "reviewer", label: "Reviewer" },
  { key: "contributor", label: "Contributor" },
] as const;

export type ModuleOwnerRole = (typeof MODULE_OWNER_ROLES)[number]["key"] | string;

/** A staff account holding a role on a governed entry (`module_owners`). */
export interface ModuleOwnerRecord {
  id: string;
  governedSlug: string;
  /** FK → `users.id` (INT). An owner without an account is not an owner. */
  userId: number;
  role: ModuleOwnerRole;
  /** At most one primary per `governedSlug` (enforced by a service hook). */
  isPrimary?: boolean;
  notes?: string | null;
  startedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

/** Editable subset of an owner used by the Team tab's add/edit form. */
export type ModuleOwnerFormData = Partial<
  Pick<ModuleOwnerRecord, "userId" | "role" | "isPrimary" | "notes" | "startedAt">
>;

// ---------------------------------------------------------------------------
// module-governance — one row per governed FACT mirrored from the Coordinator
// ---------------------------------------------------------------------------

/**
 * `module_governance.factType`. Each maps to a Coordinator artifact:
 * capability ← capabilities.json · seam/thread ← data-flow.json ·
 * elevation ← elevation-state.json · finding/skew ← catalog.json findings.
 */
export const MODULE_GOVERNANCE_FACT_TYPES = [
  { key: "capability", label: "Capabilities" },
  { key: "seam", label: "Seams" },
  { key: "elevation", label: "Elevation" },
  { key: "finding", label: "Findings" },
  { key: "skew", label: "Skew" },
  { key: "thread", label: "Threads" },
] as const;

export type ModuleGovernanceFactType =
  | (typeof MODULE_GOVERNANCE_FACT_TYPES)[number]["key"]
  | string;

/** error | warn | info — drives the chip colour and the summary rollup. */
export type ModuleGovernanceSeverity = "error" | "warn" | "info" | string;

/**
 * One governed fact about an entry (`module_governance`). SYNC-OWNED end to
 * end: the service refuses external create/patch/remove (`internalOnly()`), so
 * the UI is read-only here.
 */
export interface ModuleGovernanceFact {
  id: string;
  governedSlug: string;
  factType: ModuleGovernanceFactType;
  /** e.g. `attachments`, `estimating->production`, `W4`. */
  factKey: string;
  label?: string | null;
  /** present|partial|absent|open|done|blocked|… — per factType. */
  status?: string | null;
  severity?: ModuleGovernanceSeverity | null;
  detail?: string | null;
  /** `file:line` or prose from the Coordinator. */
  evidence?: string | null;
  sourceUpdated?: string | null;
  sortOrder?: number | null;
  syncedAt?: string;
}

// ---------------------------------------------------------------------------
// module-releases — observed published versions per package
// ---------------------------------------------------------------------------

/** published | private | absent (as observed by the feed). */
export type ModuleReleaseState = "published" | "private" | "absent" | string;

/** Where the observation came from. */
export type ModuleReleaseSource = "coordinator-feed" | "acr" | "devops" | string;

/** One observed package version for an entry (`module_releases`). Sync-owned. */
export interface ModuleReleaseRecord {
  id: string;
  governedSlug: string;
  /** e.g. `@intrasync/quality-control-api`. */
  packageName: string;
  version: string;
  state?: ModuleReleaseState | null;
  source: ModuleReleaseSource;
  observedAt: string;
  detail?: string | null;
  syncedAt?: string;
}

// ---------------------------------------------------------------------------
// module-adoption — which CLIENTS use this entry (find-only, `{ governedSlug }`)
// ---------------------------------------------------------------------------

/**
 * Which join table the adoption row came from. The three are queried together
 * so modules, tools and integrations answer the same question identically.
 *
 * `client_tools` is a naming trap: it records the CLIENT's own internal/external
 * tools (with a `url`), not the governed `tool` category — only
 * `toolType = 'internal'` rows are candidates, and a key that does not resolve
 * through the catalog is simply not adoption.
 */
export type ModuleAdoptionSource =
  | "client_modules"
  | "client_tools"
  | "client_integrations"
  | string;

/** One client using this catalog entry. */
export interface ModuleAdoptionRow {
  clientId: string;
  clientName?: string | null;
  sourceTable: ModuleAdoptionSource;
  /** The free-text key as the client row actually spells it. */
  rawKey: string;
  status?: string | null;
  isDeployable?: boolean;
  /** When the assignment first appeared (createdAt of the join row). */
  since?: string | null;
}

/** Adoption totals across all three join tables. */
export interface ModuleAdoptionTotals {
  clients: number;
  enabled: number;
  disabled: number;
}

/**
 * The `module-adoption` find() envelope — a composite object, not a
 * `{ data, total }` page. `truncated` is reported honestly rather than
 * silently capping (same contract as `mcp-reads.ts readModuleInfo`).
 */
export interface ModuleAdoptionResult {
  governedSlug: string;
  rows: ModuleAdoptionRow[];
  totals: ModuleAdoptionTotals;
  truncated?: boolean;
}

// ---------------------------------------------------------------------------
// module-activity — the merged timeline (find-only, `{ governedSlug }`)
// ---------------------------------------------------------------------------

/**
 * Which source a timeline entry came from. Each source is `.catch()`ed
 * independently by the API, so a dead source drops its rows rather than the
 * whole feed.
 */
export type ModuleActivityType =
  | "audit"
  | "communication"
  | "devops"
  | "meeting"
  | "action-item"
  | "release"
  | string;

/** One merged, render-ready timeline entry (newest first). */
export interface ModuleActivityItem {
  /** Stable list key: `<type>-<sourceId>`. */
  key: string;
  type: ModuleActivityType;
  /** ISO timestamp the timeline sorts on. */
  at: string;
  title: string;
  detail?: string | null;
  governedSlug?: string;
  /** Client the entry is also attributed to, when the source carries one. */
  clientId?: string | null;
}

// ---------------------------------------------------------------------------
// read-ai-action-items — meeting-derived work attributed to a governed entry
// ---------------------------------------------------------------------------

/**
 * One LLM/Read.ai-extracted action item, as the read-only `read-ai-action-items`
 * service projects it. Hand-written mirror of `ReadAiActionItemRecord` (cited in
 * the file header) — the service has no TypeBox schema, so its `rowToRecord`
 * whitelist is the contract this type tracks.
 *
 * ATTRIBUTION IS PER SLUG. `governedSlug`/`governedRepo` are written by the
 * API's `module-resolver.ts` and are NULL when the resolver could not decide —
 * a reviewer prompt, never a statement that the item has no module. Distinct
 * from `moduleId`/`moduleName`, which are the client's own free-text
 * `client_modules` row.
 *
 * COMPONENT ENTRIES (`ModuleCatalogRecord.componentOf` non-null) need no
 * special case here, and that is deliberate: the header comment warns that
 * REPO-level facts must not be attributed to a component, but an action item is
 * attributed by SLUG, not by repo. Exact-slug matching therefore attributes to
 * a component exactly what the resolver already resolved to it and nothing
 * more. Do not "fix" this by rolling a component's items up into its parent.
 *
 * The client rides on the OWNING MEETING (`read_ai_action_items` has no
 * clientId column), so `clientId`/`clientName`/`clientCode` are null while a
 * meeting is unmapped.
 */
export interface ModuleActionItemRecord {
  id: number;
  meetingId: number;
  /** Title of the owning meeting (left join); null if the meeting is gone. */
  meetingTitle: string | null;
  /** `read_ai` | `llm` | `gantry` — who extracted it. Free NVARCHAR. */
  source: string;
  text: string | null;
  context: string | null;
  priority: string | null;
  /** The name/email as SPOKEN in the meeting, before staff resolution. */
  assigneeName: string | null;
  assigneeEmail: string | null;
  dueDate: string | null;
  confidence: number | null;
  /** LIFECYCLE status — `'open'` | `'closed'`. Not `distributionStatus`. */
  status: string;
  /**
   * Set when a human closed the item with a reason (see
   * `views/meetingIntelligence/actionItemClose.ts`). All optional: an open item
   * carries none of them, and an API that predates the close path omits them
   * even on a closed row.
   */
  closedReason?: string | null;
  closedBy?: number | null;
  closedAt?: string | null;
  closedEvidence?: string | null;
  duplicateOfItemId?: number | null;
  /** The confidence-gated distribution tier — the item's live state. */
  distributionStatus: string;
  resolvedUserId: number | null;
  resolvedUserFirstName: string | null;
  resolvedUserLastName: string | null;
  /** `client_modules.id` — the CLIENT's row, not the governed identity. */
  moduleId: string | null;
  moduleName: string | null;
  clientId: string | null;
  clientName: string | null;
  /** `clients.code` — the estate-wide canonical client key (contract C8). */
  clientCode: string | null;
  /**
   * `read_ai_assignments.id` once distribution turned the item into an
   * assignment and sent it to somebody. NOT an approval — nothing approves
   * items since 2026-09-18.
   * Non-null is the ONLY in-record evidence that an item reached the queue —
   * there is no separate "queued" flag to invent.
   */
  assignmentId: number | null;
  // ---- Gantry deep-analysis handoff fields (null on HQ-extracted items) ----
  /** NVARCHAR(50) — a producer may know "00:14:32" without an offset. */
  transcriptTimestamp: string | null;
  videoTimestamp: string | null;
  repoPath: string | null;
  packageName: string | null;
  /** `universal` (every client) vs `client-specific`. */
  scope: string | null;
  governedSlug: string | null;
  governedRepo: string | null;
  analysisRunId: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// module-summary — one rollup row per catalog entry (find-only)
// ---------------------------------------------------------------------------

/** Latest observed version of one package (newest per package name). */
export interface ModuleSummaryRelease {
  packageName: string;
  version: string;
  state?: ModuleReleaseState | null;
  observedAt?: string | null;
}

/** Governance fact counts by severity (the Governance rollup chip). */
export interface ModuleGovernanceCounts {
  total: number;
  error: number;
  warn: number;
  info: number;
}

/**
 * Per-entry rollup from the find-only `module-summary` service — what the
 * catalog LIST joins to for its adoption / owner / governance columns.
 *
 * Field names are tolerant (optional) on purpose, the same way
 * `ClientSummaryRecord` is: the derived service lands alongside this view and
 * the ViewStore normalises whichever fields the API serialises, so a partially
 * implemented rollup degrades to blank cells instead of a crash.
 */
export interface ModuleSummaryRecord {
  governedSlug: string;
  displayName?: string | null;
  kind?: ModuleKind;
  /** Mirrored from the catalog so a summary row can be read on its own. */
  assignable?: boolean;
  componentOf?: string | null;
  /** Adoption across client_modules + client_tools + client_integrations. */
  clientCount?: number;
  enabledClientCount?: number;
  disabledClientCount?: number;
  /** Live `module_owners` rows for the slug. */
  ownerCount?: number;
  /** Open governed assignments / action items attributed to the slug. */
  openAssignmentCount?: number;
  /** Latest release per package (may be capped by the API). */
  latestReleases?: ModuleSummaryRelease[];
  governanceCounts?: ModuleGovernanceCounts;
  lastActivityAt?: string | null;
  /** The catalog document day-stamp this entry was mirrored from. */
  catalogUpdated?: string | null;
  /** Whole days between `catalogUpdated` and today — mirror staleness. */
  staleDays?: number | null;
}

// ---------------------------------------------------------------------------
// Feature-area vocabulary (runtime — the ordered lists the UI renders)
// ---------------------------------------------------------------------------

/**
 * The ModuleDetail tabs, in render order. Tabs deep-link through the QUERY
 * PARAM `?tabName=<key>` (the route's `:tab?` path segment is vestigial), so
 * every key must be URL-safe. Inactive tabs unmount — state that must survive a
 * tab switch lives on the store, never in the tab component.
 */
export const MODULE_DETAIL_TABS = [
  { key: "overview", label: "Overview" },
  { key: "adoption", label: "Adoption" },
  // Directly after Adoption on purpose: Overview says how much work is open,
  // Adoption says who runs it, Action Items shows the work itself.
  { key: "action-items", label: "Action Items" },
  { key: "team", label: "Team" },
  { key: "activity", label: "Activity" },
  { key: "comms", label: "Comms" },
  { key: "releases", label: "Releases" },
  { key: "devops", label: "DevOps" },
  { key: "governance", label: "Governance" },
  { key: "data-flow", label: "Data Flow" },
  { key: "packages", label: "Packages" },
] as const;

export type ModuleDetailTabKey = (typeof MODULE_DETAIL_TABS)[number]["key"];

/**
 * The kind facets on the catalog LIST header, in render order.
 *
 * One page serves modules, tools and integrations near-identically — kind is a
 * FACET, not a separate page. `all` is first and is the default: `core` and
 * `library` entries have no facet of their own and are reachable only under
 * `all`, where they render with a "not assignable" chip. That is deliberate —
 * the list shows everything, it just never pretends a platform entry is
 * something a client can be assigned.
 */
export const MODULE_KIND_FACETS = [
  { key: "all", label: "All" },
  { key: "module", label: "Modules" },
  { key: "tool", label: "Tools" },
  { key: "integration", label: "Integrations" },
] as const;

export type ModuleKindFacet = (typeof MODULE_KIND_FACETS)[number]["key"];
